import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [status, setStatus] = useState('loading'); // loading | valid | used | expired | invalid | success
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    api.get(`/auth/invite/${token}`)
      .then(r => { setUserInfo(r.data); setStatus('valid'); })
      .catch(err => {
        const msg = err.response?.data?.message || '';
        if (msg.includes('already been used')) setStatus('used');
        else if (msg.includes('expired')) setStatus('expired');
        else setStatus('invalid');
      });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) return toast.error('Password must be at least 8 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setSaving(true);
    try {
      await api.post('/auth/invite/accept', { token, password });
      setStatus('success');
      toast.success('Password set! You can now log in.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to set password');
    } finally {
      setSaving(false);
    }
  }

  const strength = password.length === 0 ? 0
    : password.length < 8 ? 1
    : password.length < 12 ? 2
    : /[A-Z]/.test(password) && /[0-9]/.test(password) ? 4 : 3;

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColor = ['', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-emerald-400'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 shadow-lg"
            style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
            <span className="text-white text-2xl">⚕</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Rostery</h1>
          <p className="text-sm text-slate-500 mt-1">by Rostery</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {status === 'loading' && (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Validating your invite link…</p>
            </div>
          )}

          {status === 'valid' && (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Set your password</h2>
              <p className="text-sm text-slate-500 mb-6">
                Welcome, <strong>{userInfo?.firstName}</strong>! Choose a secure password to access Rostery.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email</label>
                  <input
                    type="text"
                    readOnly
                    value={userInfo?.email || ''}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 text-slate-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">New Password</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-400 pr-10"
                      placeholder="Min. 8 characters"
                      autoFocus
                    />
                    <button type="button" onClick={() => setShowPass(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">
                      {showPass ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {password.length > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${strengthColor[strength]}`}
                          style={{ width: `${(strength / 4) * 100}%` }} />
                      </div>
                      <span className={`text-xs font-semibold ${
                        strength === 1 ? 'text-red-500' : strength === 2 ? 'text-amber-500' :
                        strength === 3 ? 'text-blue-500' : 'text-emerald-500'
                      }`}>{strengthLabel[strength]}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Confirm Password</label>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none ${
                      confirm && confirm !== password ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-primary-400'
                    }`}
                    placeholder="Re-enter your password"
                  />
                  {confirm && confirm !== password && (
                    <p className="text-xs text-red-500 mt-1">Passwords don't match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={saving || !password || !confirm}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
                  {saving ? 'Setting password…' : 'Set Password & Continue'}
                </button>
              </form>
            </>
          )}

          {status === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✓</span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Password Set!</h2>
              <p className="text-sm text-slate-500 mb-6">
                Your password has been set successfully. You can now log in to the Rostery mobile app with your email and new password.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 rounded-lg text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
                Go to Login
              </button>
            </div>
          )}

          {(status === 'used' || status === 'expired' || status === 'invalid') && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✕</span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                {status === 'used' ? 'Already Used' : status === 'expired' ? 'Link Expired' : 'Invalid Link'}
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                {status === 'used'
                  ? 'This invite link has already been used to set a password. If you need to reset your password, please contact your administrator.'
                  : status === 'expired'
                  ? 'This invite link has expired (links are valid for 72 hours). Please ask your administrator to send a new invite.'
                  : 'This invite link is invalid or has been removed. Please contact your administrator.'}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-primary-600 hover:underline">
                Go to login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
